import { z } from "zod";
import type { CustomerContextItem, CustomerContextItemInput } from "./customer-record-schemas";
import { OpaqueIdSchema } from "./identity-schemas";

// ADR-0086: every record about a tool is a typed customer-context item scoped to the tool hub.
export const HUB_RECORD_TYPES = ["call", "meeting", "email", "text", "note", "decision"] as const;
export const HubRecordTypeSchema = z.enum(HUB_RECORD_TYPES);
export type HubRecordType = z.infer<typeof HubRecordTypeSchema>;
export const HUB_RECORD_LABELS: Record<HubRecordType, string> = {
  call: "Call",
  meeting: "Meeting",
  email: "Email",
  text: "Text",
  note: "Note",
  decision: "Decision",
};
export const HubDirectionSchema = z.enum(["inbound", "outbound", "internal"]);
const DateSchema = z.iso.date();
const Timestamp = z.number().int().positive();

export const ToolHubInputSchema = z.strictObject({
  name: z.string().trim().min(1).max(160),
  summary: z.string().trim().max(2000).default(""),
  kickoffOn: DateSchema.nullable().default(null),
  goLiveOn: DateSchema.nullable().default(null),
  toolInstanceId: OpaqueIdSchema.nullable().default(null),
});
export type ToolHubInput = z.infer<typeof ToolHubInputSchema>;

export const ToolHubSchema = z.strictObject({
  id: OpaqueIdSchema,
  name: z.string().min(1).max(240),
  summary: z.string().max(2000),
  kickoffOn: DateSchema.nullable(),
  goLiveOn: DateSchema.nullable(),
  toolInstanceId: OpaqueIdSchema.nullable(),
  deployed: z.boolean(),
  // False for a deployed tool whose hub row is created on its first edit.
  saved: z.boolean(),
  createdAt: Timestamp,
  updatedAt: Timestamp,
});
export type ToolHub = z.infer<typeof ToolHubSchema>;
export const ToolHubListSchema = z.strictObject({ hubs: z.array(ToolHubSchema).max(200) });
export const ToolHubResponseSchema = z.strictObject({ hub: ToolHubSchema });

export const HubRecordInputSchema = z
  .strictObject({
    type: HubRecordTypeSchema,
    title: z.string().trim().min(1).max(240),
    occurredAt: Timestamp,
    bodyText: z.string().max(20_000).nullable().default(null),
    direction: HubDirectionSchema.nullable().default(null),
    followUpOn: DateSchema.nullable().default(null),
    replaces: OpaqueIdSchema.nullable().default(null),
    artifactIds: z.array(OpaqueIdSchema).max(10).default([]),
  })
  .refine((value) => value.type === "decision" || value.replaces === null, {
    message: "Only a decision can replace another decision.",
    path: ["replaces"],
  });
export type HubRecordInput = z.infer<typeof HubRecordInputSchema>;

const StoredHubRecordSchema = z
  .object({
    direction: HubDirectionSchema.nullable().catch(null),
    followUpOn: DateSchema.nullable().catch(null),
    replaces: OpaqueIdSchema.nullable().catch(null),
    origin: z.enum(["employee", "agent"]).catch("employee"),
    artifactIds: z.array(OpaqueIdSchema).max(10).catch([]),
  })
  .partial();

export const HubRecordSchema = z.strictObject({
  id: OpaqueIdSchema,
  type: HubRecordTypeSchema,
  title: z.string(),
  occurredAt: Timestamp,
  bodyText: z.string().nullable(),
  direction: HubDirectionSchema.nullable(),
  followUpOn: DateSchema.nullable(),
  replaces: OpaqueIdSchema.nullable(),
  origin: z.enum(["employee", "agent"]),
  artifactIds: z.array(OpaqueIdSchema),
  createdAt: Timestamp,
  updatedAt: Timestamp,
});
export type HubRecord = z.infer<typeof HubRecordSchema>;
export const HubRecordResponseSchema = z.strictObject({ record: HubRecordSchema });

export const CommercialDocumentHubLinkSchema = z.strictObject({ hubId: OpaqueIdSchema.nullable() });

/** The customer-context item that stores a hub record. */
export function hubRecordContextItem(
  input: HubRecordInput,
  hubId: string,
  origin: "employee" | "agent",
): CustomerContextItemInput {
  return {
    kind: input.type,
    title: input.title,
    bodyText: input.bodyText,
    toolId: hubId,
    sourceCreatedAt: input.occurredAt,
    metadata: {
      hubRecord: {
        direction: input.direction,
        followUpOn: input.followUpOn,
        replaces: input.replaces,
        origin,
        artifactIds: input.artifactIds,
      },
    },
  };
}

/** Reads any tool-scoped context item as a hub record; earlier free-form notes read as notes. */
export function hubRecordFromContextItem(item: CustomerContextItem): HubRecord {
  const type = HubRecordTypeSchema.safeParse(item.kind);
  const stored = StoredHubRecordSchema.safeParse(item.metadata["hubRecord"]);
  const meta = stored.success ? stored.data : {};
  return HubRecordSchema.parse({
    id: item.id,
    type: type.success ? type.data : "note",
    title: item.title,
    occurredAt: item.sourceCreatedAt ?? item.createdAt,
    bodyText: item.bodyText ?? null,
    direction: meta.direction ?? null,
    followUpOn: meta.followUpOn ?? null,
    replaces: meta.replaces ?? null,
    origin: meta.origin ?? "employee",
    artifactIds: meta.artifactIds ?? [],
    createdAt: item.createdAt,
    updatedAt: item.updatedAt,
  });
}

/** Decisions an employee recorded that no later employee decision replaces. Agent decisions are proposals. */
export function currentDecisions(records: readonly HubRecord[]) {
  const decisions = records.filter((record) => record.type === "decision");
  const replaced = new Set(
    decisions
      .filter((record) => record.origin === "employee" && record.replaces)
      .map((record) => record.replaces),
  );
  return decisions
    .filter((record) => record.origin === "employee" && !replaced.has(record.id))
    .sort((a, b) => b.occurredAt - a.occurredAt);
}

export type HubNextInput = {
  hub: Pick<ToolHub, "kickoffOn" | "goLiveOn" | "deployed">;
  records: readonly HubRecord[];
  documents: readonly {
    kind: "quote" | "order_form" | "change_order";
    updatedAt: number;
    signedOn: string | null;
  }[];
  billing: { hasTerms: boolean; collecting: boolean } | null;
  now: number;
};
export type HubNextStep = { rule: string; text: string };

const DAY = 86_400;
const isoDay = (seconds: number) => new Date(seconds * 1000).toISOString().slice(0, 10);
const daysBetween = (from: string, to: string) =>
  Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / (DAY * 1000));
const plural = (count: number, word: string) => `${count} ${word}${count === 1 ? "" : "s"}`;

type NextContext = HubNextInput & {
  today: string;
  byTime: HubRecord[];
  signed: boolean;
};
type NextRule = (context: NextContext) => HubNextStep | null;

function replyOwed({ byTime, today }: NextContext): HubNextStep | null {
  const lastInbound = byTime.find((record) => record.direction === "inbound");
  const lastOutbound = byTime.find((record) => record.direction === "outbound");
  if (!lastInbound || (lastOutbound && lastOutbound.occurredAt >= lastInbound.occurredAt))
    return null;
  const days = Math.max(0, daysBetween(isoDay(lastInbound.occurredAt), today));
  return {
    rule: "reply_owed",
    text: `Reply owed: ${lastInbound.title} (${days === 0 ? "today" : `${plural(days, "day")} ago`})`,
  };
}

// A follow-up is open until something newer is recorded.
function followUpDue({ byTime, today }: NextContext): HubNextStep | null {
  const due = byTime.find(
    (record) =>
      record.followUpOn !== null &&
      record.followUpOn <= today &&
      !byTime.some((later) => later.occurredAt > record.occurredAt),
  );
  return due ? { rule: "follow_up", text: `Follow up: ${due.title}` } : null;
}

function chaseSignature({ documents, signed, now }: NextContext): HubNextStep | null {
  const waiting = documents.some(
    (document) =>
      !document.signedOn && document.kind !== "quote" && now - document.updatedAt > 5 * DAY,
  );
  return !signed && waiting
    ? { rule: "chase_signature", text: "Chase the signature on the order form" }
    : null;
}

const setUpBilling: NextRule = ({ signed, billing }) =>
  signed && billing && !billing.hasTerms
    ? { rule: "set_up_billing", text: "Signed · set up billing terms" }
    : null;

const scheduleKickoff: NextRule = ({ signed, hub }) =>
  signed && !hub.kickoffOn
    ? { rule: "schedule_kickoff", text: "Signed · schedule the kickoff" }
    : null;

function goLiveSoon({ hub, today }: NextContext): HubNextStep | null {
  if (!hub.goLiveOn || hub.deployed) return null;
  const days = daysBetween(today, hub.goLiveOn);
  return days >= 0 && days <= 14
    ? { rule: "go_live_soon", text: `Go-live in ${plural(days, "day")}, not deployed yet` }
    : null;
}

const startBilling: NextRule = ({ signed, billing, hub, today }) =>
  signed && billing?.hasTerms && !billing.collecting && hub.goLiveOn && hub.goLiveOn <= today
    ? { rule: "start_billing", text: "Live · start the subscription" }
    : null;

function checkIn({ signed, byTime, now }: NextContext): HubNextStep | null {
  const latest = byTime[0];
  return signed && latest && now - latest.occurredAt > 14 * DAY
    ? { rule: "check_in", text: "No contact in two weeks · check in" }
    : null;
}

const NEXT_RULES: readonly NextRule[] = [
  replyOwed,
  followUpDue,
  chaseSignature,
  setUpBilling,
  scheduleKickoff,
  goLiveSoon,
  startBilling,
  checkIn,
];

/** One derived next step for a hub; the first matching rule wins. Never a checklist. */
export function hubNextStep(input: HubNextInput): HubNextStep {
  const context: NextContext = {
    ...input,
    today: isoDay(input.now),
    byTime: [...input.records].sort((a, b) => b.occurredAt - a.occurredAt),
    signed: input.documents.some((document) => document.signedOn),
  };
  for (const rule of NEXT_RULES) {
    const step = rule(context);
    if (step) return step;
  }
  return { rule: "nothing_due", text: "Nothing due" };
}

const CommercialKindSchema = z.enum(["quote", "order_form", "change_order"]);
export const HubDocumentSchema = z.strictObject({
  id: OpaqueIdSchema,
  kind: CommercialKindSchema,
  title: z.string(),
  reference: z.string(),
  revision: z.number().int().positive(),
  updatedAt: Timestamp,
  filing: z
    .strictObject({
      revision: z.number().int().positive(),
      signedOn: DateSchema,
      customerDocumentId: OpaqueIdSchema,
    })
    .nullable(),
});
export type HubDocument = z.infer<typeof HubDocumentSchema>;
export const HubDocumentsSchema = z.strictObject({
  linked: z.array(HubDocumentSchema).max(200),
  // Unlinked documents whose tool or project name matches the hub name.
  suggested: z.array(HubDocumentSchema).max(200),
});
export type HubDocuments = z.infer<typeof HubDocumentsSchema>;
export const HubBillingSchema = z.strictObject({ hasTerms: z.boolean(), collecting: z.boolean() });

export function hubNextDocuments(documents: readonly HubDocument[]): HubNextInput["documents"] {
  return documents.map((document) => ({
    kind: document.kind,
    updatedAt: document.updatedAt,
    signedOn: document.filing?.signedOn ?? null,
  }));
}

// ADR-0088: the customer page reads every hub's next step, contract, and latest records at once.
export const HubRecordSummarySchema = HubRecordSchema.pick({
  id: true,
  type: true,
  title: true,
  occurredAt: true,
});
export type HubRecordSummary = z.infer<typeof HubRecordSummarySchema>;
export const HubOverviewSchema = z.strictObject({
  hub: ToolHubSchema,
  next: z.strictObject({ rule: z.string().max(40), text: z.string().max(400) }),
  // Null when the employee cannot read billing, so contract state is withheld.
  documents: z.array(HubDocumentSchema).max(200).nullable(),
  latestRecords: z.array(HubRecordSummarySchema).max(5),
});
export type HubOverview = z.infer<typeof HubOverviewSchema>;
export const HubOverviewListSchema = z.strictObject({
  hubs: z.array(HubOverviewSchema).max(200),
  // False while the customer's isolated context is still being set up.
  recordsAvailable: z.boolean(),
});

export const LibraryHubAssignmentSchema = z.strictObject({
  documentId: OpaqueIdSchema,
  hubId: OpaqueIdSchema,
  kind: z.enum(["knowledge", "agreement", "quote"]),
  title: z.string(),
  shared: z.boolean(),
  // True for a signed copy that belongs to its order form's hub.
  inherited: z.boolean(),
  updatedAt: Timestamp,
});
export type LibraryHubAssignment = z.infer<typeof LibraryHubAssignmentSchema>;
export const HubAssignmentsSchema = z.strictObject({
  library: z.array(LibraryHubAssignmentSchema).max(500),
  commercial: z
    .array(z.strictObject({ documentId: OpaqueIdSchema, hubId: OpaqueIdSchema }))
    .max(500),
});
export type HubAssignments = z.infer<typeof HubAssignmentsSchema>;

/** The order form and change orders that make up a hub's contract, newest change first. */
export function hubContract(documents: readonly HubDocument[]) {
  const orderForms = documents.filter((document) => document.kind === "order_form");
  return {
    orderForm: orderForms.find((document) => document.filing) ?? orderForms[0] ?? null,
    changeOrders: documents
      .filter((document) => document.kind === "change_order")
      .sort((a, b) => b.updatedAt - a.updatedAt),
  };
}
